import axios from 'axios';

// Configuration de base
//const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://apps.prod.atlanticds-sn.com/smartboutik/api';

// ✅ CORRECTION : Instance axios avec configuration robuste mais compatible
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // Timeout augmenté pour les requêtes lentes
  headers: {
    'Content-Type': 'application/json',
  },
});


// ✅ CORRECTION : Intercepteur de requête amélioré
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // ✅ CORRECTION : Nettoyer les paramètres pour éviter les erreurs 400
    if (config.params) {
      const cleanedParams = {};
      Object.keys(config.params).forEach(key => {
        const value = config.params[key];
        if (value !== null && value !== undefined && value !== '') {
          cleanedParams[key] = value;
        }
      });
      config.params = cleanedParams;
    }
    
    // Log pour debug (seulement en développement)
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 Requête API:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        params: config.params,
        data: config.data
      });
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Erreur dans la configuration de la requête:', error);
    return Promise.reject(error);
  }
);

// ✅ CORRECTION : Intercepteur de réponse amélioré
api.interceptors.response.use(
  (response) => {
    // Log pour debug (seulement en développement)
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Réponse API réussie:', {
        url: response.config.url,
        status: response.status,
        data: response.data
      });
    }
    return response;
  },
  (error) => {
    // Log détaillé de l'erreur
    console.error('❌ Erreur API:', {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      code: error.code
    });
    
    // Gestion spécifique des erreurs d'authentification
    // ✅ Endpoints publics : un 401 ici = identifiants invalides, PAS une session expirée
    const requestUrl = error.config?.url || '';
    const isPublicAuthEndpoint =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/register') ||
      requestUrl.includes('/auth/forgot-password') ||
      requestUrl.includes('/auth/reset-password');

    // Gestion spécifique des erreurs d'authentification (session expirée)
    if (error.response?.status === 401 && !isPublicAuthEndpoint) {
      console.warn('🔓 Erreur 401 - Token expiré ou invalide');
      
      // Nettoyer le stockage local
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('selectedBoutique');
      
      // Rediriger vers login seulement si on n'y est pas déjà
      if (!window.location.pathname.includes('/login')) {
        console.log('🔄 Redirection vers la page de connexion...');
        window.location.href = '/login';
      }
      
      return Promise.reject(new Error('Session expirée. Veuillez vous reconnecter.'));
    }
    
    // Gestion des erreurs de validation (400)
    if (error.response?.status === 400) {
      console.warn('⚠️ Erreur de validation:', error.response.data);
      return Promise.reject(error);
    }
    
    // Gestion des erreurs d'autorisation (403)
    if (error.response?.status === 403) {
      console.warn('🚫 Accès refusé - Permissions insuffisantes');
      return Promise.reject(new Error('Vous n\'avez pas les permissions pour cette action.'));
    }
    
    // Gestion des erreurs de ressource non trouvée (404)
    if (error.response?.status === 404) {
      console.warn('🔍 Ressource non trouvée');
      return Promise.reject(new Error('Ressource non trouvée.'));
    }
    
    // Gestion des erreurs de serveur (5xx)
    if (error.response?.status >= 500) {
      console.error('🔥 Erreur serveur:', error.response.status);
      return Promise.reject(new Error('Erreur du serveur. Veuillez réessayer plus tard.'));
    }
    
    // Gestion des erreurs réseau
    if (error.code === 'NETWORK_ERROR') {
      console.error('🌐 Erreur réseau');
      return Promise.reject(new Error('Problème de connexion. Vérifiez votre réseau.'));
    }
    
    // Gestion des erreurs de timeout
    if (error.code === 'ECONNABORTED') {
      console.error('⏰ Timeout de requête');
      return Promise.reject(new Error('Délai d\'attente dépassé. Veuillez réessayer.'));
    }
    
    return Promise.reject(error);
  }
);

// ✅ CORRECTION : Wrapper API amélioré avec meilleure gestion des erreurs
const apiCall = {
  // GET request
  async get(url, params = {}) {
    try {
      const response = await api.get(url, { params });
      return {
        success: true,
        data: response.data.data || response.data,
        status: response.status,
        message: response.data.message || 'Succès'
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        status: error.response?.status || 'network',
        message: error.response?.data?.message || error.message || 'Erreur lors de la récupération',
        error: error.response?.data || error
      };
    }
  },

  /**
   * ✅ NOUVELLE MÉTHODE: Upload de fichier avec callback de progression
   * @param {string} endpoint - Endpoint relatif (ex: '/admin/boutiques/123/logo')
   * @param {FormData} formData - Données du formulaire avec fichier
   * @param {Function} onProgress - Callback de progression (optionnel)
   * @returns {Promise<Object>} Réponse de l'API
   */
  upload: async (endpoint, formData, onProgress = null) => {
    try {
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          // ✅ IMPORTANT: Ne pas définir 'Content-Type' pour FormData - axios le fait automatiquement
        },
        timeout: 30000, // 30 secondes pour les uploads
      };

      // ✅ CORRECTION CRITIQUE : Vérifier que onProgress est une fonction AVANT de l'utiliser
      if (onProgress && typeof onProgress === 'function') {
        config.onUploadProgress = (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          // ✅ Appeler la fonction de callback seulement si elle existe
          onProgress(percentCompleted);
        };
      }

      // Log pour debug
      if (process.env.NODE_ENV === 'development') {
        console.log('📤 Upload vers:', API_BASE_URL + endpoint);
        console.log('📦 FormData entries:');
        for (let [key, value] of formData.entries()) {
          console.log(`  ${key}:`, value instanceof File ? `File(${value.name})` : value);
        }
      }
      
      const response = await axios.post(API_BASE_URL + endpoint, formData, config);
      
      // Log pour debug
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Upload réussi:', response.data);
      }
      
      // ✅ Retourner la réponse dans le format standard SmartBoutik
      if (response.data && typeof response.data === 'object') {
        return response.data;
      }
      
      // Fallback si la réponse n'est pas dans le format attendu
      return {
        success: true,
        data: response.data,
        message: 'Upload réussi'
      };
      
    } catch (error) {
      console.error('❌ Erreur upload:', error);
      
      // ✅ Gestion d'erreurs détaillée
      if (error.response?.data) {
        // Le serveur a répondu avec une erreur
        return {
          success: false,
          message: error.response.data.message || 'Erreur lors de l\'upload',
          error: error.response.data.error || 'UPLOAD_ERROR',
          details: error.response.data.details
        };
      }
      
      if (error.code === 'ECONNABORTED') {
        // Timeout
        return {
          success: false,
          message: 'Timeout - le fichier est peut-être trop volumineux',
          error: 'TIMEOUT_ERROR'
        };
      }
      
      if (error.code === 'NETWORK_ERROR') {
        // Erreur réseau
        return {
          success: false,
          message: 'Erreur de connexion au serveur',
          error: 'NETWORK_ERROR'
        };
      }
      
      // Erreur générique
      return {
        success: false,
        message: error.message || 'Erreur lors de l\'upload',
        error: 'UPLOAD_ERROR'
      };
    }
  },

  /**
   * ✅ MÉTHODE UTILITAIRE: Validation de fichier côté client
   * @param {File} file - Fichier à valider
   * @param {Object} options - Options de validation
   * @returns {Object} Résultat de la validation
   */
  validateFile: (file, options = {}) => {
    const {
      maxSize = 5 * 1024 * 1024, // 5MB par défaut
      allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
      allowedExtensions = ['jpg', 'jpeg', 'png', 'webp']
    } = options;

    if (!file || !(file instanceof File)) {
      return {
        valid: false,
        message: 'Fichier requis'
      };
    }

    // Validation du type MIME
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        message: `Format non supporté. Utilisez: ${allowedTypes.join(', ')}`
      };
    }

    // Validation de l'extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      return {
        valid: false,
        message: `Extension non supportée. Utilisez: ${allowedExtensions.join(', ')}`
      };
    }

    // Validation de la taille
    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / (1024 * 1024));
      return {
        valid: false,
        message: `Fichier trop volumineux (${maxSizeMB}MB maximum)`
      };
    }

    return {
      valid: true,
      message: 'Fichier valide',
      file: {
        name: file.name,
        size: file.size,
        type: file.type,
        extension: extension
      }
    };
  },

  // POST request
  async post(url, data = {}) {
    try {
      const response = await api.post(url, data);
      return {
        success: true,
        data: response.data.data || response.data,
        status: response.status,
        message: response.data.message || 'Création réussie'
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        status: error.response?.status || 'network',
        message: error.response?.data?.message || error.message || 'Erreur lors de la création',
        error: error.response?.data || error
      };
    }
  },

  // PUT request
  async put(url, data = {}) {
    try {
      const response = await api.put(url, data);
      return {
        success: true,
        data: response.data.data || response.data,
        status: response.status,
        message: response.data.message || 'Mise à jour réussie'
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        status: error.response?.status || 'network',
        message: error.response?.data?.message || error.message || 'Erreur lors de la mise à jour',
        error: error.response?.data || error
      };
    }
  },

  // PATCH request
  async patch(url, data = {}) {
    try {
      const response = await api.patch(url, data);
      return {
        success: true,
        data: response.data.data || response.data,
        status: response.status,
        message: response.data.message || 'Modification réussie'
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        status: error.response?.status || 'network',
        message: error.response?.data?.message || error.message || 'Erreur lors de la modification',
        error: error.response?.data || error
      };
    }
  },

  // DELETE request
  async delete(url) {
    try {
      const response = await api.delete(url);
      return {
        success: true,
        data: response.data.data || response.data,
        status: response.status,
        message: response.data.message || 'Suppression réussie'
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        status: error.response?.status || 'network',
        message: error.response?.data?.message || error.message || 'Erreur lors de la suppression',
        error: error.response?.data || error
      };
    }
  }
};

// ✅ ENDPOINTS CORRIGÉS - Structure complète
export const endpoints = {
  // ========== AUTHENTIFICATION (AuthController) ==========
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    register: '/auth/register',
    profile: '/auth/profile', // ✅ GET et PUT pour le profil
    changePassword: '/auth/change-password', // ✅ POST pour changer le mot de passe
    verify: '/auth/verify', // ✅ GET pour vérifier le token
    refresh: '/auth/refresh', // ✅ POST pour rafraîchir le token
    preferences: '/auth/preferences', // ✅ GET et PUT pour les préférences
    loginHistory: '/auth/login-history', // ✅ GET pour l'historique des connexions
    uploadPhoto: '/auth/profile/photo', // ✅ POST pour upload photo de profil
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password'
  },
  
  // ========== SUPER ADMIN (AdminController) ==========
  admin: {
    // Gestion des boutiques
    boutiques: {
      create: '/admin/boutiques',
      list: '/admin/boutiques',
      detail: (id) => `/admin/boutiques/${id}`,
      update: (id) => `/admin/boutiques/${id}`,
      archive: (id) => `/admin/boutiques/${id}/archive`,
      activate: (id) => `/admin/boutiques/${id}/activate`,
      suspend: (id) => `/admin/boutiques/${id}/suspend`,
      delete: (id) => `/admin/boutiques/${id}`,
      stats: (id) => `/admin/boutiques/${id}/stats`,
      // ✅ NOUVEAUX ENDPOINTS pour la configuration
      uploadLogo: (id) => `/admin/boutiques/${id}/logo`,
      updateColors: (id) => `/admin/boutiques/${id}/colors`,
      getConfig: (id) => `/admin/boutiques/${id}/config`,
      // ✅ NOUVEAUX : Gestion des ventes de boutique
      deleteAllSales: (id) => `/admin/boutiques/${id}/sales`,
      getSalesStats: (id) => `/admin/boutiques/${id}/sales/stats`
    },
    
    // Gestion globale des utilisateurs
    users: {
      create: '/admin/users',
      deactivate: (id) => `/admin/users/${id}/deactivate`,
      activate: (id) => `/admin/users/${id}/activate`,
      update: (id) => `/admin/users/${id}`,
      delete: (id) => `/admin/users/${id}`,
      byBoutique: (boutiqueId) => `/admin/boutiques/${boutiqueId}/users`
    },
    
    // Dashboard super admin
    dashboard: '/admin/dashboard',
    
    // Statistiques globales
    stats: {
      global: '/admin/stats',
      boutiques: '/admin/stats/boutiques',
      users: '/admin/stats/users',
      performance: '/admin/stats/performance'
    }
  },
  
  // ========== GESTIONNAIRE (ManagerController) ==========
  manager: {
    // Gestion des produits
    products: {
      list: '/manager/products',
      create: '/manager/products',
      detail: (id) => `/manager/products/${id}`,
      update: (id) => `/manager/products/${id}`,
      deactivate: (id) => `/manager/products/${id}/deactivate`,
      delete: (id) => `/manager/products/${id}`,
      search: '/manager/products/search',
      import: '/manager/products/import',                    // ✅ NOUVEAU
      importTemplate: '/manager/products/import/template',   // ✅ NOUVEAU
      
      // ✅ NOUVEAUX ENDPOINTS POUR L'ÉTIQUETAGE
      generateLabels: '/manager/products/labels/generate',
      validateBarcodes: '/manager/products/barcodes/validate',
      scanByBarcode: (barcode) => `/manager/products/scan/${encodeURIComponent(barcode)}`,
      barcodeStats: '/manager/products/barcodes/stats',
      withoutBarcodes: '/manager/products/without-barcodes',
      generateBarcode: '/manager/products/barcode/generate',
      
      // Pour la recherche par code lors des ventes
      byCode: (code) => `/manager/products/by-code/${encodeURIComponent(code)}`,
    },
    
    // Gestion des catégories
    categories: {
      list: '/manager/categories',
      create: '/manager/categories',
      update: (id) => `/manager/categories/${id}`,
      delete: (id) => `/manager/categories/${id}`,
      getById: (id) => `/manager/categories/${id}`,
      suggest: '/manager/categories/suggest',
    },
    
    // ✅ CORRECTION : Gestion du stock avec endpoints alternatifs
    stock: {
      list: '/manager/stock',
      update: (id) => `/manager/stock/${id}`,
      adjust: (id) => `/manager/stock/${id}/adjust`,
      alerts: '/manager/stock/alerts',
      movements: '/manager/stock/movements',
      movement: '/manager/stock/movement',
      history: '/manager/stock/history',
      threshold: (id) => `/manager/stock/${id}/threshold`,
      inventory: '/manager/stock/inventory'
    },
    
    // ✅ CORRECTION : Gestion des ventes (vue manager) - structure corrigée
    sales: {
      create: '/employee/sales',
      list: '/employee/sales',
      detail: (id) => `/employee/sales/${id}`,
      cancel: (id) => `/employee/sales/${id}/cancel`,
      updatePayment: (id) => `/employee/sales/${id}/payment`,
      credits: '/employee/sales/credits',
      creditHistory: (id) => `/employee/sales/${id}/credit-history`
    },
    
    // Gestion des clients (vue manager)
    customers: {
      list: '/manager/customers',
      create: '/manager/customers',
      search: '/manager/customers/search',
      detail: (id) => `/manager/customers/${id}`,
      update: (id) => `/manager/customers/${id}`
    },
    
    // Gestion des employés
    employees: {
      list: '/manager/employees',
      create: '/manager/employees',
      detail: (id) => `/manager/employees/${id}`,
      update: (id) => `/manager/employees/${id}`,
      deactivate: (id) => `/manager/employees/${id}/deactivate`
    },
    
    // Dashboard manager
    dashboard: '/manager/dashboard',
    
    // Statistiques boutique
    stats: {
      today: '/manager/stats/today',
      topProducts: '/manager/stats/top-products',
      creditBalance: '/manager/stats/credit-balance',
      teamActivity: '/manager/team/activity'
    }
  },
  
  // ========== EMPLOYÉ (EmployeeController) ==========
  employee: {
    // Consultation des produits
    products: {
      list: '/employee/products',
      detail: (id) => `/employee/products/${id}`,
      search: '/employee/products/search',
      stock: (id) => `/employee/products/${id}/stock`,
      scanByBarcode: (barcode) => `/employee/products/scan/${encodeURIComponent(barcode)}`,
      byCode: (code) => `/employee/products/by-code/${encodeURIComponent(code)}`,
    },
    
    // Consultation des catégories
    categories: {
      list: '/employee/categories'
    },
    
    // Stock pour employés
    stock: {
      alerts: '/employee/stock/alerts'
    },
    
    // Gestion des ventes (interface principale)
    sales: {
      create: '/employee/sales',
      detail: (id) => `/employee/sales/${id}`,
      list: '/employee/sales',
      cancel: (id) => `/employee/sales/${id}/cancel`,
      updatePayment: (id) => `/employee/sales/${id}/payment`,
      credits: '/employee/sales/credits'
    },
    
    // Gestion des clients (interface vente)
    customers: {
      list: '/employee/customers',
      create: '/employee/customers',
      search: '/employee/customers/search',
      detail: (id) => `/employee/customers/${id}`,
      update: (id) => `/employee/customers/${id}`
    },
    
    // Dashboard employé
    dashboard: '/employee/dashboard',
    
    // Statistiques employé
    stats: {
      topProducts: '/employee/stats/top-products'
    }
  },
  
  // ========== COMMUN (CommonController) ==========
  common: {
    // Informations boutique courante
    boutique: {
      current: '/common/boutique/current',
      info: '/common/boutique/info'
    },
    
    // Catégories publiques
    categories: '/common/categories',
    
    // Utilitaires
    health: '/common/health',
    version: '/common/version'
  },
  
  // ========== ALIASES POUR COMPATIBILITÉ ==========
  boutiques: {
    list: '/admin/boutiques',
    create: '/admin/boutiques',
    update: (id) => `/admin/boutiques/${id}`,
    detail: (id) => `/admin/boutiques/${id}`,
    delete: (id) => `/admin/boutiques/${id}`,
    current: '/common/boutique/current',
    stats: (id) => `/admin/boutiques/${id}/stats`
  },
  
  products: {
    list: '/manager/products',
    listEmployee: '/employee/products',
    create: '/manager/products',
    update: (id) => `/manager/products/${id}`,
    detail: (id) => `/manager/products/${id}`,
    delete: (id) => `/manager/products/${id}`,
    search: '/manager/products/search',
    searchEmployee: '/employee/products/search',
    stock: (id) => `/employee/products/${id}/stock`
  },
  
  categories: {
    list: '/manager/categories',
    listEmployee: '/employee/categories',
    listCommon: '/common/categories',
    create: '/manager/categories',
    update: (id) => `/manager/categories/${id}`,
    delete: (id) => `/manager/categories/${id}`
  },
  
  customers: {
    list: '/employee/customers',
    listManager: '/manager/customers',
    create: '/employee/customers',
    update: (id) => `/employee/customers/${id}`,
    detail: (id) => `/employee/customers/${id}`,
    search: '/employee/customers/search'
  },
  
  sales: {
    list: '/employee/sales',
    create: '/employee/sales',
    detail: (id) => `/employee/sales/${id}`,
    cancel: (id) => `/employee/sales/${id}/cancel`,
    updatePayment: (id) => `/employee/sales/${id}/payment`,
    credits: '/employee/sales/credits',
    stats: '/manager/sales/stats'
  },
  
  // Stock avec fallbacks
  stock: {
    list: '/manager/stock',
    listEmployee: '/employee/stock/alerts',
    movements: '/manager/stock/movements',
    alerts: '/manager/stock/alerts',
    alertsEmployee: '/employee/stock/alerts',
    update: (id) => `/manager/stock/${id}`,
    detail: (id) => `/manager/stock/${id}`,
    threshold: (id) => `/manager/stock/${id}/threshold`,
    inventory: '/manager/stock/inventory'
  },
  
  stats: {
    dashboard: '/manager/dashboard',
    today: '/manager/stats/today',
    topProducts: '/manager/stats/top-products',
    creditBalance: '/manager/stats/credit-balance',
    teamActivity: '/manager/team/activity'
  },
  
  // Dashboard & Analytics
  dashboard: {
    superAdmin: '/admin/dashboard',
    manager: '/manager/dashboard',
    employee: '/employee/dashboard'
  }
};

// ✅ Utilitaires API
export const buildUrl = (endpoint, params = {}) => {
  let url = endpoint;
  Object.keys(params).forEach(key => {
    url = url.replace(`:${key}`, params[key]);
  });
  return url;
};

// Fonction pour vérifier la connectivité
export const checkApiHealth = async () => {
  try {
    const response = await apiCall.get('/common/health');
    return response.success;
  } catch (error) {
    console.error('❌ API non accessible:', error);
    return false;
  }
};

// Configuration des timeouts pour différents types d'opérations
export const timeouts = {
  SHORT: 5000,    // Auth, health check
  MEDIUM: 15000,  // CRUD operations
  LONG: 30000,    // Upload, export
  VERY_LONG: 60000 // Import massif
};

// Export de l'instance axios pour des usages spécifiques
export { api, apiCall };
export default apiCall;

/**
 * Helper pour gérer les erreurs spécifiques aux codes-barres
 */
export const barcodeApiHelpers = {
  /**
   * Gérer les erreurs de scan
   */
  handleScanError: (error) => {
    if (error.response?.status === 404) {
      return {
        success: false,
        message: 'Produit non trouvé avec ce code-barres',
        errorCode: 'PRODUCT_NOT_FOUND'
      };
    }
    
    if (error.response?.status === 400) {
      return {
        success: false,
        message: 'Code-barres invalide',
        errorCode: 'INVALID_BARCODE'
      };
    }
    
    return {
      success: false,
      message: 'Erreur lors du scan du code-barres',
      errorCode: 'SCAN_ERROR'
    };
  },

  /**
   * Valider un code-barres côté client avant envoi
   */
  validateBarcodeFormat: (barcode) => {
    if (!barcode || typeof barcode !== 'string') {
      return { valid: false, error: 'Code-barres requis' };
    }

    const cleaned = barcode.trim();
    
    if (cleaned.length < 4) {
      return { valid: false, error: 'Code-barres trop court (minimum 4 caractères)' };
    }

    if (cleaned.length > 50) {
      return { valid: false, error: 'Code-barres trop long (maximum 50 caractères)' };
    }

    // Caractères autorisés pour les codes-barres
    if (!/^[A-Za-z0-9\-_.]+$/.test(cleaned)) {
      return { valid: false, error: 'Code-barres contient des caractères invalides' };
    }

    return { valid: true, barcode: cleaned };
  },

  /**
   * Formater une requête de génération d'étiquettes
   */
  formatLabelRequest: (productIds, format, copies, options = {}) => {
    return {
      productIds: Array.isArray(productIds) ? productIds : [productIds],
      format: format || 'A4',
      copies: copies || 1,
      includePrice: options.includePrice !== false,
      includeBarcode: options.includeBarcode !== false,
      includeDescription: options.includeDescription || false
    };
  }
};